import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import R from "ramda";
import humps from "humps";
import clsx from "clsx";
import { useFormik } from "formik";
import { Button, TextField, FormHelperText } from "@material-ui/core";
import {
  useRecurly,
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
} from "@recurly/react-recurly";

import { appRoutes } from "routes";
import { useScreen } from "hooks";
import { initialValues } from "forms/RecurlyPaymentForm";
import PurchaseDisclaimer from "components/PurchaseDisclaimer";
import PaymentMethods from "components/PaymentMethods";

import CountrySelect from "./components/CountrySelect";
import useStyles, { recurlyElementStyle } from "./useStyles";

const RecurlyForm = (props) => {
  const { onSubmit, disabled, error, isUpdate } = props;
  const classes = useStyles();
  const recurly = useRecurly();
  const formRef = useRef();
  const { isTablet, isMobile } = useScreen();
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFour, setLastFour] = useState("");
  const [cardBrand, setBrand] = useState("");

  const formik = useFormik({ initialValues });
  const { handleChange, values } = formik;

  const recurlyInputStyle = isMobile
    ? recurlyElementStyle
    : { ...recurlyElementStyle, padding: "10px 14px", fontSize: "17px" };

  const handleSubmit = (event) => {
    if (event.preventDefault) event.preventDefault();
    setLoading(true);
    recurly.token(formRef.current, (err, token) => {
      if (err) {
        setValidationErrors(err.fields.map(humps.camelize));
      } else {
        setValidationErrors([]);
        onSubmit({ ...values, token, lastFour, cardBrand }, recurly);
      }
      setLoading(false);
    });
  };

  const handleCardChange = (cardCreds) => {
    const { valid, lastFour: lastFourDigits, brand } = cardCreds;
    if (valid) {
      setLastFour(lastFourDigits);
      setBrand(brand);
    }
    return null;
  };

  const hasError = (field) => !!validationErrors.includes(field);

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit} ref={formRef}>
        <input type="hidden" name="recurly-token" data-recurly="token" />
        <div className={classes.grid}>
          <PaymentMethods />
          <div className={classes.nameGrid}>
            <TextField
              type="text"
              placeholder="First name"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                "data-recurly": "first_name",
              }}
              id="firstName"
              onChange={handleChange}
              value={values.firstName}
              error={hasError("firstName")}
              helperText={hasError("firstName") && "Invalid First Name"}
            />
            <TextField
              type="text"
              placeholder="Last name"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                "data-recurly": "last_name",
              }}
              id="lastName"
              onChange={handleChange}
              value={values.lastName}
              error={hasError("lastName")}
              helperText={hasError("lastName") && "Invalid Last Name"}
            />
          </div>
          <div className={classes.cardGrid}>
            <div className={clsx({ [classes.recurlyFieldError]: hasError("number") })}>
              <CardNumberElement
                className={classes.recurlyInput}
                style={{
                  ...recurlyInputStyle,
                  placeholder: {
                    ...recurlyInputStyle.placeholder,
                    content: "Credit Card Number",
                  },
                }}
                onChange={handleCardChange}
              />
              {hasError("number") && <FormHelperText error>Invalid Credit Card Number</FormHelperText>}
            </div>
            <div className={clsx({ [classes.recurlyFieldError]: hasError("cvv") })}>
              <CardCvvElement
                style={{
                  ...recurlyInputStyle,
                  placeholder: {
                    ...recurlyInputStyle.placeholder,
                    content: "CVV",
                  },
                }}
              />
              {hasError("cvv") && <FormHelperText error>Invalid CVV</FormHelperText>}
            </div>
          </div>
          <div className={classes.expireGrid}>
            <span className={classes.expires}>Card expires:</span>
            <div className={clsx({ [classes.recurlyFieldError]: hasError("month") })}>
              <CardMonthElement
                inputType="mobileSelect"
                style={{
                  ...recurlyInputStyle,
                  placeholder: {
                    ...recurlyInputStyle.placeholder,
                    content: "MM",
                  },
                }}
              />
            </div>
            <div className={clsx({ [classes.recurlyFieldError]: hasError("year") })}>
              <CardYearElement
                inputType="mobileSelect"
                style={{
                  ...recurlyInputStyle,
                  placeholder: {
                    ...recurlyInputStyle.placeholder,
                    content: "YY",
                  },
                }}
              />
            </div>
            <TextField
              type="text"
              placeholder="ZIP Code"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                "data-recurly": "postal_code",
              }}
              id="postalCode"
              onChange={handleChange}
              value={values.postalCode}
              error={hasError("postalCode")}
            />
          </div>
        </div>
        {(hasError("month") || hasError("year")) && <FormHelperText error>Invalid Expiration Date</FormHelperText>}
        {hasError("postalCode") && <FormHelperText error>Invalid Postal Code</FormHelperText>}

        <div className={classes.countrySelect}>
          <CountrySelect onChange={handleChange} value={values.country} error={hasError("country")} />
        </div>

        {isUpdate ? (
          <div className={classes.termsOfSale}>
            By clicking &quot;Update&quot;, I agree to JibJab&apos;s{" "}
            <Link to={appRoutes.termsOfSalePath()} target="_blank">
              Terms of Sale
            </Link>
            .
          </div>
        ) : (
          <PurchaseDisclaimer />
        )}

        {!R.isNil(error) && <FormHelperText className={classes.commonError}>{error}</FormHelperText>}

        <div className={classes.actions}>
          <Button
            disabled={loading || disabled}
            fullWidth
            color="secondary"
            variant="contained"
            type="submit"
            size={isTablet ? "medium" : "large"}
          >
            {isUpdate ? "Update" : "Purchase"}
          </Button>
        </div>
      </form>
    </div>
  );
};

RecurlyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  isUpdate: PropTypes.bool,
};

RecurlyForm.defaultProps = {
  disabled: false,
  isUpdate: false,
};

export default RecurlyForm;
